import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  Box,
  Button,
  TextField,
  IconButton,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Stepper,
  Step,
  StepLabel,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  AlertTitle,
  CircularProgress,
  Tooltip,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import TotalModalPay from './TotalModalPay';
import { Accordion, AccordionDetails, AccordionSummary, Checkbox } from '@material-ui/core';
import InfoIcon from '@mui/icons-material/Info';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningIcon from '@mui/icons-material/Warning';
import ExtensionIcon from '@mui/icons-material/Extension';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { calculateExpirationDate, getPlanLevel, MODIFICATION, PURCHASE, RENEWAL } from './helpers';
import { isEmpty } from 'lodash';
import useDeviceDetect from '../../hooks/useDeviceDetect';
import MercadoPagoForm from './MercadoPagoForm';
import { getUserConfig } from '../../services/client';
import { useHistory } from 'react-router-dom';

const ModalPay = ({
  openModalPay,
  handleOpenCloseModalPay,
  handlePurchase,
  selectedPlan,
  userInfo,
  purchasingPlan,
  currency,
  products,
  setProducts,
  usdCotization,
  fixedCosts,
  purchaseType,
  lastPurchase,
  proporsionalPayment,
  initialProducts
}) => {  
  const mercadopagoImg = '/images/mercadopago-logo.svg';
  const { isMobile } = useDeviceDetect(720);
  const [activeStep, setActiveStep] = useState(0);
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const MONTHLY = 1;
  const ANNUAL = 2;
  const ANNUAL_LABEL = 'Anual';
  const [planType, setPlanType] = useState(MONTHLY);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [totalInARS, setTotalInARS] = useState(0);
  const annualMultiplier = userInfo?.multiplicador_anual;
  const actualLevelPlan = userInfo?.plan_level;
  const MERCADO_PAGO = 'mercadopago';
  const TRANSFERENCIA = 'transferencia';
  const [previousAmountPaid, setPreviousAmountPaid] = useState(0);
  const [changes, setChanges] = useState(false);
  const [isRecurrent, setIsRecurrent] = useState(false);
  const hasMercadoPagoStep = selectedPaymentMethod === MERCADO_PAGO && calculatedTotal > 0;
  const steps = [
    "Seleccionar productos",
    "Vista previa",
    "Elegir método de pago",
    ...(hasMercadoPagoStep && isRecurrent ? ["Pago con MercadoPago"] : []),
  ];
  const maxStep = steps.length - 1;
  const [userConfig, setUserConfig] = useState({});
  const [loadingUserConfig, setLoadingUserConfig] = useState(false);
  const history = useHistory();
  const RECURRENT = 'Recurrente';
  const REACTIVATE = "REACTIVATE";
  
  useEffect(() => {
    let productsAux = [...products];
    productsAux.forEach(
      product => {
        product.newPrice = purchaseType === MODIFICATION ? (product.price * (planType === ANNUAL ? 12 : 1)) * (proporsionalPayment || 1) : (product.price * (planType === ANNUAL ? 12 : 1));
      }
    );
    setProducts(productsAux);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planType]);

  useEffect(() => {
    if (purchaseType === MODIFICATION || purchaseType === RENEWAL) {
      loadLastPurchase();
    }
    if(lastPurchase?.tipo_ultima_compra === RECURRENT && purchaseType === MODIFICATION ){
      setIsRecurrent(true);
      setSelectedPaymentMethod(MERCADO_PAGO);
    }
    if(lastPurchase?.tipo_ultima_compra === RECURRENT && purchaseType === REACTIVATE ){
      setIsRecurrent(true);
      setSelectedPaymentMethod(MERCADO_PAGO);
      loadLastPurchase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const fetchUserConfig = async () => {
      try {
        setLoadingUserConfig(true);
        const res = await getUserConfig();
        setUserConfig(res.data);
        setLoadingUserConfig(false);
      } catch (error) {
        console.error('Error fetching user config:', error);
        setLoadingUserConfig(false);
      }
    };
    fetchUserConfig();
  }, []);

  useEffect(() => {
    const hasChanges = products.some(arr => {
      const arr2 = initialProducts.find(o => o.id === arr.id);
      return arr2 && arr.quantity !== arr2.quantity;
    });
    setChanges(hasChanges);
  }, [products, initialProducts])

  const loadLastPurchase = () => {
    setPlanType(lastPurchase?.duracion === ANNUAL_LABEL ? ANNUAL : MONTHLY);
    let amountPaid = 0;
    if (!isEmpty(lastPurchase.perks)) {
      Object.entries(lastPurchase.perks).forEach(([id, cantidad]) => {
        let product = products.find(product => product.id === parseInt(id, 10));
        if (product) {
          product.quantity = cantidad;
          product.min = cantidad;
          amountPaid += product.newPrice * cantidad;
        }
      });
      setProducts(products);
      setPreviousAmountPaid(amountPaid);
    }
  }

  const save = (cardToken = null) => {
    let perksSelected = getPerksSelected();
    let purchase = {
      perks: perksSelected,
      duracion: planType,
      tipo: purchaseType,
      transferencia: selectedPaymentMethod === TRANSFERENCIA ? true : false,
    };

    if(cardToken){
      purchase.card_token = cardToken;
    }

    if (purchaseType !== MODIFICATION) {
      purchase.plan = selectedPlan.id;
    }
    handlePurchase(purchase);
  };

  const getPerksSelected = () => {
    let productsAux = products.map(p => ({ ...p }));
    let productsSelected = productsAux.filter(product => {
      product.quantity -= (purchaseType === MODIFICATION ? (product.min ?? 0) : 0);
      return product.quantity > 0;
    });
    let perks = productsSelected.map(product => {
      return {
        perk_id: product.id,
        cantidad: product.quantity
      }
    })
    return perks
  }

  const handlePlanChange = (_, newValue) => {
    if (newValue) {
      if(newValue === ANNUAL) {
        setIsRecurrent(false);
      }
      setPlanType(newValue);
    }
  };

  const updateQuantity = (id, change) => {
    setProducts(products.map(product =>
      product.id === id ? { ...product, quantity: Math.max(0, product.quantity + change) } : product
    ));
  };

  const formatPrice = (price) => {
    if (price) {
      if (currency === "ARS") {
        return `$ ${formatToLocaleString(price)} ARS`;
      } else {
        return `U$S ${formatToLocaleString(price)}`;
      }
    }
  };

  const formatToLocaleString = (number) => {
    return Number(number).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  const productsByTipe = (arr, key) => {
    if (arr?.length === 0) return;
    // agrupa por tipo
    const grouped = arr.reduce((acc, item) => {
      (acc[item[key]] = acc[item[key]] || []).push(item);
      return acc;
    }, {});

    // ordena por cardinalidad
    Object.keys(grouped).forEach(groupKey => {
      grouped[groupKey].sort((a, b) => a.cardinalidad - b.cardinalidad);
    });

    return grouped;
  };

  const getLabelType = (type) => {
    switch (type) {
      case "Medidor Extra":
        return "Puntos de Medición Extra"
      case "Subcuentas Extra":
        return "Cuentas Invitado Extra"
      default:
        return type;
    }
  }

  const handleCredits = (creditRefund, manualCredits) => {
    let credits = manualCredits;
    if(isRecurrent) {
      credits = 0
    }else if (purchaseType === PURCHASE && (actualLevelPlan <= selectedPlan?.level)) {
      credits += creditRefund
    }
    return credits
  }

  // ------ NUEVAS FUNCIONES PARA FLUJO DE STEPS ------
  const handleNext = () => {
    if (activeStep === 2 && selectedPaymentMethod === MERCADO_PAGO && calculatedTotal > 0) {
      setActiveStep(activeStep + 1); // Pasa al paso de MercadoPagoForm
    } else if (activeStep < maxStep) {
      setActiveStep(activeStep + 1);
    }
  };
  const handleBack = () => setActiveStep(activeStep - 1);
  // --------------------------------------------------
  const handleEditBillingData = () => {
    handleOpenCloseModalPay();
    history.push('/configuracion-cuenta');
  }

  return (
    <Dialog
      open={openModalPay}
      onClose={handleOpenCloseModalPay}
      fullWidth
      maxWidth="md"
    >
      <Box p={5} sx={{ borderRadius: 2, backgroundColor: '#FAFAFA' }}>
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          <Typography
            variant="h5"
            textAlign="center"
            fontWeight="bold"
            color="primary"
            mr={1}
          >
            Plan {selectedPlan?.name}
          </Typography>
          {actualLevelPlan > selectedPlan?.level && (
            <Tooltip title="Este plan tiene prestaciones inferiores al plan que su cuenta posee actualmente, el mismo sera asignado al concluir el periodo de compra actual.">
              <WarningIcon
                style={{ color: '#FFCE08', fontSize: 28, cursor: 'pointer' }}
              />
            </Tooltip>
          )}
        </Box>

        {
          lastPurchase?.dias_adeudados > 0 && purchaseType === RENEWAL && (
            <Alert severity="error" style={{ marginTop: 12 }}>
              <AlertTitle style={{ fontWeight: 'bold' }}> Tienes {lastPurchase?.dias_adeudados} días adeudados, si renuevas ahora la fecha del próximo vencimiento será el {calculateExpirationDate(planType === ANNUAL
                ? lastPurchase?.prox_vencimiento_anual
                : lastPurchase?.prox_vencimiento_mensual)}.</AlertTitle>
            </Alert>
          )
        }

        {
          (purchaseType === MODIFICATION || purchaseType === RENEWAL) && (userInfo?.dias_restantes > 0) && (
            <Box position="relative" mt={2} mb={3}>
              <Typography
                variant="body1"
                fontWeight="bold"
                position="absolute"
                left={0}
                top="50%"
                sx={{ transform: 'translateY(-50%)' }}
              >
                Días restantes: {userInfo?.dias_restantes}
              </Typography>

              <Box display="flex" justifyContent="center">
                <Box backgroundColor="#E0F2FF" color="primary" p={2} borderRadius={2}>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {`${purchaseType === MODIFICATION ? 'Modificación' : 'Renovación'} de plan actual`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
        }

        <Stepper activeStep={activeStep} sx={{ my: 3 }}>
          {steps.map((label, idx) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Paso 1: Seleccionar productos */}
        {activeStep === 0 && (
          <>
            <Box display="flex" justifyContent="center" mt={3}>
              <ToggleButtonGroup
                value={planType}
                exclusive
                onChange={handlePlanChange}
                color="primary"
                disabled={planType === ANNUAL && userInfo?.dias_restantes > 30}
              >
                <ToggleButton value={MONTHLY}>Mensual</ToggleButton>
                <ToggleButton value={ANNUAL}>
                  <Box sx={{
                    position: "absolute",
                    top: -10,
                    right: -12,
                    backgroundColor: "#D32A31",
                    color: "white",
                    fontSize: "0.7rem",
                    padding: "2px 6px",
                    borderRadius: "16px",
                    whiteSpace: "nowrap",
                  }}>
                    15% OFF
                  </Box>
                  Anual
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {planType === ANNUAL && userInfo?.dias_restantes > 30 &&
              <Alert severity="warning" style={{ marginTop: 12, marginBottom: 12 }}>
                <AlertTitle>No es posible realizar el cambio de modalidad anual a mensual cuando el usuario posee más de un mes de servicio ya abonado en el período vigente.</AlertTitle>
              </Alert>              
            }

            {!userInfo?.puede_crear_recurrente &&
              (
                userInfo?.total_deuda > 0 &&
                <Alert severity="warning" style={{ marginTop: 12 }}>
                  <AlertTitle>Para acceder a la funcionalidad de realizar débitos automáticos, debes abonar tu deuda actual.</AlertTitle>
                </Alert>
              )
            }

           {(userInfo.puede_crear_recurrente) && <Box display="flex" justifyContent="center" mb={2} mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRecurrent}
                    onChange={(_, checked) => {
                      setIsRecurrent(checked)
                      setSelectedPaymentMethod(MERCADO_PAGO);
                    }}
                    color="primary"
                    disabled={planType === ANNUAL || (lastPurchase?.tipo_ultima_compra === RECURRENT && (purchaseType === MODIFICATION || purchaseType === REACTIVATE))}
                  />
                }
                label={
                  <span style={{ fontWeight: 500,display:'flex',alignItems:'center' }}>
                    Débito automático <Tooltip title="Activa esta opción si querés que la suscripción se renueve automáticamente con MercadoPago (solo disponible para planes mensuales)"><InfoIcon sx={{ fontSize: 17, color: "primary.main", ml: 0.5 }}/></Tooltip>
                  </span>
                }
              />
            </Box>}

            <Box display="flex" alignItems="center" mb={2}>
              <ExtensionIcon color="primary" sx={{ mr: 1, fontSize: 22 }} />
              <Typography fontSize={18} fontWeight="bold">
                Adicionales
              </Typography>
            </Box>

            {
              ((purchaseType === MODIFICATION || purchaseType === RENEWAL) && lastPurchase?.perks_chord && Object.keys(lastPurchase.perks_chord).length > 0) && (
                <Box mb={2} p={2} bgcolor="#E0F7FA" borderRadius={2}>
                  Tienes contratados los siguientes adicionales en tu plan actual:
                  <br />
                  {Object.keys(lastPurchase.perks_chord).length > 0
                    ? Object.entries(lastPurchase.perks_chord)
                      .map(([key, value]) => `${key}`)
                      .join(', ')
                    : '-'}
                </Box>
              )
            }

            <Grid container spacing={2} px={4}>
              {Object.entries(productsByTipe(products, 'tipo'))?.map(([tipo, items]) => (
                <Box width="100%" mt={2} key={tipo}>
                  <Accordion key={tipo}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">{getLabelType(tipo)}</Typography>
                        <Typography style={{ fontSize: '12px', marginLeft: '10px' }}>
                          {items.reduce((acc, item) => acc + item.quantity, 0)} seleccionados
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {items.map((product) => (
                          <Grid item xs={12} sm={4} key={product.id}>
                            <Card
                              sx={{
                                p: 2,
                                textAlign: "center",
                                boxShadow: 3,
                                borderRadius: 2,
                                minHeight: 150,
                                position: "relative",
                                overflow: "visible",
                              }}
                            >
                              {product.label_descuento > 0 && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: -10,
                                    right: -12,
                                    backgroundColor: "#D32A31",
                                    color: "white",
                                    fontSize: "0.7rem",
                                    padding: "4px 6px",
                                    borderRadius: "16px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {product.label_descuento}% OFF
                                </Box>
                              )}
                              <CardContent>
                                <Typography fontSize={17} fontWeight="bold">
                                  {product.descripcion}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatPrice(product.newPrice)}
                                </Typography>
                              </CardContent>
                              <Box display="flex" alignItems="center" justifyContent="center">
                                <IconButton disabled={(product.min === product.quantity)} onClick={() => updateQuantity(product.id, -1)} color="error">
                                  <RemoveIcon />
                                </IconButton>
                                <TextField
                                  value={product.quantity}
                                  size="small"
                                  sx={{ width: 50 }}
                                  onChange={(e) => {
                                    const newQuantity = parseInt(e.target.value);
                                    if (!isNaN(newQuantity) && newQuantity >= 0) {
                                      setProducts(
                                        products.map((p) =>
                                          p.id === product.id ? { ...p, quantity: newQuantity } : p
                                        )
                                      );
                                    }
                                    if (e.target.value < product.min) {
                                      setProducts(
                                        products.map((p) =>
                                          p.id === product.id ? { ...p, quantity: product.min } : p
                                        )
                                      );
                                    }
                                  }}
                                />
                                <IconButton onClick={() => updateQuantity(product.id, 1)} color="primary">
                                  <AddIcon />
                                </IconButton>
                              </Box>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Box>

              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <TotalModalPay
              planType={planType}
              products={products}
              selectedPlan={selectedPlan}
              credit={handleCredits(userInfo?.creditRefund, userInfo?.manualCredits)}
              annualMultiplier={annualMultiplier}
              setCalculatedTotal={setCalculatedTotal}
              currency={currency}
              usdCotization={usdCotization}
              setTotalInARS={setTotalInARS}
              fixedCosts={fixedCosts}
              purchaseType={purchaseType}
              proporsionalPayment={proporsionalPayment}
              lastPurchase={lastPurchase}
              previousAmountPaid={previousAmountPaid}
            />
          </>
        )}

        {/* Paso 2: Vista previa */}
        {activeStep === 1 && (
          <>
            <Typography
              variant="h6"
              textAlign="center"
              fontWeight="bold"
              mb={2}
            >
              Resumen de tu selección
            </Typography>

            <Grid container spacing={2} px={4}>
              {products.filter(p => p.quantity - (purchaseType === MODIFICATION ? (p.min ? p.min : 0) : 0) > 0).length > 0 ? (
                products
                  .filter(p => p.quantity - (purchaseType === MODIFICATION ? (p.min ? p.min : 0) : 0) > 0)
                  .map(product => (
                    <Grid item xs={12} sm={6} key={product.id}>
                      <Card
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          boxShadow: 3,
                          borderRadius: 2,
                        }}
                      >
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold">
                            {product.descripcion}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cantidad: {product.quantity - (purchaseType === MODIFICATION ? (product.min ? product.min : 0) : 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total: {formatPrice((product.newPrice * product.quantity) - (purchaseType === MODIFICATION ? (product.min ? (product.newPrice * product.min) : 0) : 0))}{' '}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  width="100%"
                >
                  No has seleccionado ningún extra.
                </Typography>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <TotalModalPay
              planType={planType}
              products={products}
              selectedPlan={selectedPlan}
              credit={handleCredits(userInfo?.creditRefund, userInfo?.manualCredits)}
              annualMultiplier={annualMultiplier}
              setCalculatedTotal={setCalculatedTotal}
              currency={currency}
              usdCotization={usdCotization}
              setTotalInARS={setTotalInARS}
              fixedCosts={fixedCosts}
              purchaseType={purchaseType}
              proporsionalPayment={proporsionalPayment}
              lastPurchase={lastPurchase}
              previousAmountPaid={previousAmountPaid}
            />
          </>
        )}

        {/* Paso 3: Elegir método de pago */}
        {activeStep === 2 && (
          <>
            <Typography
              variant="h6"
              textAlign="center"
              fontWeight="bold"
              mb={3}
            >
              Elige tu método de pago
            </Typography>
      
            <Box mb={3} px={3}>
              <Card sx={{ 
                boxShadow: 2,
                borderRadius: 2,
                p: 2,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="primary"
                  >
                    Datos de Facturación
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleEditBillingData}
                    sx={{
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5
                    }}
                  >
                    Editar
                  </Button>
                </Box>
                
                {loadingUserConfig ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5} bgcolor="#f8f9fa" borderRadius={1}>
                        <Typography variant="body2" fontWeight="medium" color="text.primary">
                          CUIT:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                          {userConfig.cuit || 'No especificado'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5} bgcolor="#f8f9fa" borderRadius={1}>
                        <Typography variant="body2" fontWeight="medium" color="text.primary">
                          Razón Social:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color="text.primary">
                          {userConfig.razon_social || 'No especificado'}
                        </Typography>
                      </Box>
                    </Grid>                
                  </Grid>
                )}
              </Card>
            </Box>

            <Box display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
              {calculatedTotal > 0 ? (
                <>
                  <Card
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      boxShadow: 3,
                      borderRadius: 2,
                      width: isMobile ? '100%' : '45%',
                      cursor: 'pointer',
                      border:
                        selectedPaymentMethod === MERCADO_PAGO
                          ? '2px solid #007aff'
                          : '2px solid transparent',
                    }}
                    onClick={() => setSelectedPaymentMethod(MERCADO_PAGO)}
                  >
                    <CardContent>
                      <img
                        src={mercadopagoImg}
                        alt="MercadoPago"
                        width={isMobile ? "170px" : "220px"}
                        height={isMobile ? "70px" : "120px"}
                      />
                    </CardContent>
                  </Card>

                  <Card
                    disabled={isRecurrent}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      boxShadow: 3,
                      borderRadius: 2,
                      width: isMobile ? '100%' : '45%',
                      cursor: isRecurrent ? 'not-allowed' : 'pointer',
                      opacity: isRecurrent ? 0.5 : 1,
                      border: selectedPaymentMethod === TRANSFERENCIA
                        ? '2px solid #007aff'
                        : '2px solid transparent',
                      pointerEvents: isRecurrent ? 'none' : 'auto',
                      transition: 'opacity 0.2s',
                      backgroundColor: isRecurrent ? '#f5f5f5' : '#fff',
                    }}
                    onClick={() => !isRecurrent && setSelectedPaymentMethod(TRANSFERENCIA)}
                  >
                    <CardContent>
                      <Box
                        height={'80px'}
                        display={'flex'}
                        flexDirection={'column'}
                        justifyContent={'center'}
                        alignItems={'center'}
                      >
                        <AccountBalanceIcon
                          sx={{ fontSize: isMobile ? 32 : 43, color: 'primary.main' }}
                        />
                      </Box>
                      <Typography fontSize={isMobile ? 14 : 16} fontWeight="bold">
                        Transferencia Bancaria
                      </Typography>
                    </CardContent>
                  </Card>

                </>
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p={2}
                  mb={2}
                  bgcolor="#E0F7FA"
                  borderRadius={2}
                >
                  <InfoIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="primary" fontWeight="bold">
                    El pago será gratuito ya que cuentas con crédito suficiente.
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            <TotalModalPay
              planType={planType}
              products={products}
              selectedPlan={selectedPlan}
              credit={handleCredits(userInfo?.creditRefund, userInfo?.manualCredits)}
              annualMultiplier={annualMultiplier}
              setCalculatedTotal={setCalculatedTotal}
              currency={currency}
              usdCotization={usdCotization}
              setTotalInARS={setTotalInARS}
              fixedCosts={fixedCosts}
              purchaseType={purchaseType}
              proporsionalPayment={proporsionalPayment}
              lastPurchase={lastPurchase}
              previousAmountPaid={previousAmountPaid}
            />

            {currency === 'USD' &&
              (
                <Alert severity="warning" style={{ marginTop: 12 }}>
                  <AlertTitle>El pago debe realizarse en <b>PESOS ARGENTINOS</b>. Según la cotizacion del día el pago será de ${formatToLocaleString(totalInARS)} ARS</AlertTitle>
                </Alert>
              )}
          </>
        )}

        {/* Paso 4: MercadoPagoForm */}
        {activeStep === 3 && hasMercadoPagoStep && (
          <Box mt={3} display="flex" flexDirection="column" alignItems="center">
            <MercadoPagoForm
              amount={calculatedTotal}
              email={userInfo?.email}
              save={save}
              purchasingPlan={purchasingPlan}
            />
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        {(purchaseType === MODIFICATION && changes === false) && (
          <Box mb={2}>
            <Alert severity="warning" style={{ width: '100%' }}>
              <AlertTitle>No has modificado nada de tu plan actual.</AlertTitle>
              Para continuar, debes agregar por lo menos un adicional.
            </Alert>
          </Box>
        )}
        <Box display="flex" justifyContent="end" gap={2}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              variant="outlined"
            >
              Atrás
            </Button>
          )}
          {activeStep < maxStep ? (
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={(purchaseType === MODIFICATION && changes === false) || (activeStep === 2 && calculatedTotal > 0 && !selectedPaymentMethod)}
            >
              Siguiente
            </Button>
          ) : (purchasingPlan && !isRecurrent) ? (
            <Box>
              <CircularProgress />
            </Box>
          ) : 
          activeStep !== 3 && 
          (
            <Button
              onClick={() => save(null)}
              variant="contained"
              color="primary"
              disabled={(calculatedTotal > 0 && !selectedPaymentMethod)}
            >
              Confirmar compra
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ModalPay;
